/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { createRouter, createWebHistory } from "vue-router";
import { START_LOCATION } from "vue-router";
import {
  useUserStore,
  useConfigStore,
  useSelectionStore,
} from "@pristy/pristy-libvue";
import App from "./App.vue";

const PdfPage = () => import("./pages/PdfPage.vue");
const ImagePage = () => import("./pages/ImagePage.vue");
const FolderPage = () => import("./pages/FolderPage.vue");
const VideoPage = () => import("./pages/VideoPage.vue");
const MarkdownPage = () => import("./pages/MarkdownPage.vue");
const HtmlPage = () => import("./pages/HtmlPage.vue");
const TextPage = () => import("./pages/TextPage.vue");

const routes = [
  {
    path: "/",
    name: "app",
    component: App,
    children: [
      {
        path: "",
        name: "welcome",
        meta: {
          requiresAuth: true,
        },
        component: () => import("./pages/StartPage.vue"),
      },
      {
        path: "/mes-espaces",
        name: "mes-espaces",
        meta: {
          requiresAuth: true,
        },
        component: () => import("./pages/WorkspacesPage.vue"),
      },
      {
        path: "/mes-espaces/:id/membres",
        name: "espace-membres",
        meta: {
          requiresAuth: true,
        },
        component: () => import("./pages/WorkspaceMemberPage.vue"),
      },
      {
        path: "/mes-espaces/:id",
        name: "navigation",
        meta: {
          requiresAuth: true,
        },
        component: FolderPage,
      },
      {
        path: "/mes-espaces/:id/:FolderId",
        name: "navigation-dossier",
        meta: {
          requiresAuth: true,
        },
        component: FolderPage,
      },
      {
        path: "/pdf/:id",
        name: "consultation-pdf",
        meta: {
          requiresAuth: true,
        },
        component: PdfPage,
      },
      {
        path: "/pdf/:id/:versionId",
        name: "consultation-pdf-version",
        meta: {
          requiresAuth: true,
        },
        component: PdfPage,
      },
      {
        path: "/image/:id",
        name: "consultation-image",
        meta: {
          requiresAuth: true,
        },
        component: ImagePage,
      },
      {
        path: "/image/:id/:versionId",
        name: "consultation-image-version",
        meta: {
          requiresAuth: true,
        },
        component: ImagePage,
      },
      {
        path: "/video/:id",
        name: "consultation-video",
        meta: {
          requiresAuth: true,
        },
        component: VideoPage,
      },
      {
        path: "/video/:id/:versionId",
        name: "consultation-video-version",
        meta: {
          requiresAuth: true,
        },
        component: VideoPage,
      },
      {
        path: "/md/:id",
        name: "md-editor",
        meta: {
          requiresAuth: true,
        },
        component: MarkdownPage,
      },
      {
        path: "/md/:id",
        name: "md-editor-version",
        meta: {
          requiresAuth: true,
        },
        component: MarkdownPage,
      },
      {
        path: "/html/:id",
        name: "html-editor",
        meta: {
          requiresAuth: true,
        },
        component: HtmlPage,
      },
      {
        path: "/html/:id",
        name: "html-editor-version",
        meta: {
          requiresAuth: true,
        },
        component: HtmlPage,
      },
      {
        path: "/text/:id",
        name: "text-editor",
        meta: {
          requiresAuth: true,
        },
        component: TextPage,
      },
      {
        path: "/text/:id/:versionId",
        name: "text-editor-version",
        meta: {
          requiresAuth: true,
        },
        component: TextPage,
      },
      {
        path: "/edit/:id",
        name: "edit-collabora",
        meta: {
          requiresAuth: true,
        },
        component: () => import("./pages/EditCollaboraPage.vue"),
      },
      {
        path: "/edit/:id/:versionId",
        name: "edit-collabora-version",
        redirect: { name: "consultation-pdf-version" },
      },
      {
        path: "/recherche",
        name: "recherche",
        meta: {
          requiresAuth: true,
        },
        component: () => import("./pages/RecherchePage.vue"),
      },
      {
        path: "/:pathMatch(.*)*",
        name: "not-found-page",
        meta: {
          requiresAuth: true,
        },
        component: () => import("./pages/NotFoundPages.vue"),
      },
    ],
  },
  {
    path: "/login",
    name: "login",
    component: () => import("./pages/LoginPage.vue"),
  },
  {
    path: "/error/:id",
    name: "error",
    component: () => import("./pages/ErrorPage.vue"),
    props: true,
  },
];

const router = createRouter({
  history: createWebHistory("/espaces"),
  routes,
});

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function connectKeycloak() {
  const user = useUserStore();
  // We wait for Keycloak init, then we can call all methods safely
  while (user.keycloak == null || user.keycloak.createLoginUrl === null) {
    await sleep(100);
  }
  if (user.keycloak.authenticated) {
    while (!user.isLoggedIn) {
      await sleep(100);
      await user.validateSession();
    }
    if (user.person === null) {
      await user.getPerson();
    }
  } else {
    const loginUrl = user.keycloak.createLoginUrl();
    window.location.replace(loginUrl);
  }
}

router.beforeEach(function () {
  window.scrollTo(0, 0);
});

import {
  loadLocaleMessages,
  setI18nLanguage,
  i18n,
} from "@/services/TranslationService";
import { useNavigationStore } from "@pristy/pristy-libvue";

// navigation guards
router.beforeEach(async (to, from) => {
  if (from === START_LOCATION) {
    // Load locale only on first page laoding
    let locale =
      to.params.locale ||
      localStorage?.getItem("user.language") ||
      window?.navigator?.language ||
      "fr";

    // load locale messages
    if (!i18n.global.availableLocales.includes(locale)) {
      await loadLocaleMessages(i18n, locale);
    }

    // set i18n language
    setI18nLanguage(i18n, locale);
  }
});

router.beforeEach(async (to, from) => {
  const config = useConfigStore();
  const user = useUserStore();

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (config.AUTH === "keycloak" || config.AUTH === "lemonldap") {
      await connectKeycloak();
    } else {
      // Get Person to check if we are authenticate
      await user.getPerson();
      if (!user.isLoggedIn) {
        return { name: "login", query: { target: to.path } };
      }
    }
  }

  // Don't go to login if we are loggedIn
  if (user.isLoggedIn && from.name && to.name === "login") {
    return { name: "welcome" };
  }
});

router.beforeEach(async (to) => {
  await useSelectionStore().resetSelection();
  if (to.name.startsWith("navigation")) {
    await useNavigationStore().load(to.params.id, to.params.FolderId, false);
  }
});

router.beforeEach((to) => {
  let pageTitle = "";
  const nav = useNavigationStore();
  switch (to.name) {
    case "welcome":
      pageTitle = "";
      break;
    case "mes-espaces":
      pageTitle = `${i18n.global.t("window.workspacePageTitle")}`;
      break;
    case "espace-membres":
      pageTitle = `${i18n.global.t("window.workspaceMemberPageTitle")}`;
      break;
    case "navigation":
      pageTitle = nav.workspace.title;
      break;
    case "navigation-dossier":
      pageTitle = nav.currentNode.name;
      break;
    case "recherche":
      pageTitle = `${i18n.global.t("menu.recherche")}`;
      break;
    case "not-found-page":
      pageTitle = "Page Not Found";
      break;
    case "login":
      pageTitle = `${i18n.global.t("login.connexion")}`;
      break;
    case "error":
      pageTitle = "Error";
      break;
    default:
      pageTitle = "";
  }
  const config = useConfigStore();
  window.document.title = `${pageTitle} | ${config.APP_ROOT_TITLE}`;
});

export default router;
